import React, { useState } from "react";
import { Lock, Mail, User, ArrowRight, Loader2 } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

const AdminLogin = () => {
  const { login } = useAdminAuth();

  // Mode Toggle (Login vs Register)
  const [isRegister, setIsRegister] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const [status, setStatus] = useState({ loading: false, error: "", success: "" });

  // Handle Input Change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ loading: true, error: "", success: "" });

    const endpoint = isRegister ? "/register" : "/login";
    const url = `https://luxuriyahomes.com/api/admin/auth${endpoint}`; // Port 1001 hai tumhara

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      // Agar response TEXT hai (Success message) ya JSON (Token)
      const text = await response.text(); 
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text }; // Fallback agar plain string aaye
      }

      if (response.ok) {
        if (isRegister) {
          // Registration Success
          setStatus({ 
            loading: false, 
            error: "", 
            success: "Registration Successful! Please wait for Super Admin approval." 
          });
          // Form clear kar do
          setFormData({ name: "", email: "", password: "" });
        } else {
          // Login Success (Context Update karo with NAME)
          login(data.token, data.role, data.name);
        }
      } else {
        // Error from Backend
        setStatus({ loading: false, error: data.message || "Authentication Failed", success: "" });
      }

    } catch (err) {
      console.error(err);
      setStatus({ loading: false, error: "Server Error. Check Backend Connection.", success: "" });
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0c0c] flex items-center justify-center px-4 font-[Serif] relative overflow-hidden">
      
      {/* Background Decor (Subtle Glow) */}
      <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-amber-600/10 rounded-full blur-[100px]" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-zinc-800/20 rounded-full blur-[100px]" />

      <div className="w-full max-w-md bg-[#161616] border border-zinc-800 p-8 md:p-10 rounded-2xl shadow-2xl relative z-10">
        
        {/* Top Gradient Strip */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-600 to-amber-400" />

        <div className="text-center mb-10">
          <p className="text-amber-500 text-xs tracking-[0.3em] uppercase mb-2">
            Luxuriya Homes
          </p>
          <h2 className="text-3xl text-white font-light">
            {isRegister ? "Join The Team" : "Admin Portal"}
          </h2>
        </div>

        {/* Success Message */}
        {status.success && (
          <div className="mb-6 p-4 bg-green-900/20 border border-green-800 rounded text-center">
            <p className="text-green-400 text-sm font-bold">{status.success}</p>
            <button 
              onClick={() => { setIsRegister(false); setStatus({ ...status, success: "" }); }}
              className="text-xs text-green-200 underline mt-2 hover:text-white"
            >
              Go to Login
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Name Field (Only for Register) */}
          {isRegister && (
            <div className="relative group animate-in fade-in slide-in-from-top-4 duration-300">
              <User className="absolute left-0 top-3 text-zinc-500 group-focus-within:text-amber-500 transition-colors" size={20} />
              <input 
                name="name"
                type="text" 
                placeholder="Full Name"
                className="w-full bg-transparent border-b border-zinc-700 py-3 pl-8 text-white focus:outline-none focus:border-amber-500 transition-all placeholder:text-zinc-600"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
          )}

          {/* Email Input */}
          <div className="relative group">
            <Mail className="absolute left-0 top-3 text-zinc-500 group-focus-within:text-amber-500 transition-colors" size={20} />
            <input 
              name="email"
              type="email" 
              placeholder="Email Address"
              className="w-full bg-transparent border-b border-zinc-700 py-3 pl-8 text-white focus:outline-none focus:border-amber-500 transition-all placeholder:text-zinc-600"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          {/* Password Input */}
          <div className="relative group">
            <Lock className="absolute left-0 top-3 text-zinc-500 group-focus-within:text-amber-500 transition-colors" size={20} />
            <input 
              name="password"
              type="password" 
              placeholder="Password"
              className="w-full bg-transparent border-b border-zinc-700 py-3 pl-8 text-white focus:outline-none focus:border-amber-500 transition-all placeholder:text-zinc-600"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          {/* Error Message */}
          {status.error && <p className="text-red-400 text-xs text-center tracking-wide animate-pulse">{status.error}</p>}

          {/* Submit Button */}
          <button 
            disabled={status.loading}
            className="w-full bg-gradient-to-r from-amber-600 to-amber-500 text-black font-bold py-4 uppercase tracking-widest hover:from-amber-500 hover:to-amber-400 transition-all flex items-center justify-center gap-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed rounded"
          >
            {status.loading ? <Loader2 className="animate-spin" /> : (isRegister ? "Submit Request" : "Login Access")} 
            {!status.loading && <ArrowRight size={18} />}
          </button>
        </form>

        {/* Toggle Login/Register */}
        <div className="mt-8 text-center border-t border-zinc-800 pt-6">
          <p className="text-zinc-500 text-sm">
            {isRegister ? "Already hold an account?" : "Need team access?"}
          </p>
          <button 
            onClick={() => {
              setIsRegister(!isRegister);
              setStatus({ loading: false, error: "", success: "" }); // Reset status
            }}
            className="text-amber-500 font-bold uppercase text-xs tracking-widest mt-2 hover:text-amber-400 transition-colors"
          >
            {isRegister ? "Login Here" : "Register New Account"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AdminLogin;